import { useState, FC, Key, useEffect, SetStateAction } from 'react';
import { white } from '@styles/colors';
import { bold, medium } from '@styles/fontWeights';
import {
  Stack,
  Typography,
  Divider,
  Button,
  CircularProgress,
  Box,
  Alert,
} from '@mui/material';
import { useGetCompanyOnboardingDocs } from '@features/boostMyJobs/hooks/useGetCompanyOnboardingDocs';
import { Add } from '@mui/icons-material';
import OnboardingPackageModal from '@features/digitalOnboarding/components/OnboardingPackageModal/OnboardingPackageModal';
import PayrollPartnerModal from '@features/digitalOnboarding/components/PayrollPartnerModal/PayrollPartnerModal';
import SpinLoader from '@components/SpinLoader/SpinLoader';
import {
  OnboardingPackage,
  PayrollPartners,
} from '@features/digitalOnboarding';
import PayrollPartnerDeleteModal from '@features/digitalOnboarding/components/PayrollPartnerDeleteModal/PayrollPartnerDeleteModal';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import MonitorOutlinedIcon from '@mui/icons-material/MonitorOutlined';
import EnterpriseOnboardingPackages from './EnterpriseOnboardingPackages';
import EnterpriseOnboardingPayrollPartners from './EnterpriseOnboardingPayrollPartners';
import NewHireNotification from '@features/digitalOnboarding/components/NewHireNotification/NewHireNotification';

type EnterpriseOnboardingDocumnetsProps = {
  selectedCompanyId: number;
  companyPlan: number;
};

const EnterpriseOnboardingDocumnets: FC<EnterpriseOnboardingDocumnetsProps> = ({
  selectedCompanyId,
  companyPlan,
}) => {
  const [errorText, setErrorText] = useState('');
  const [isDeletingModalOpen, setIsDeletingModalOpen] = useState(false);
  const [isOnboardingPackageModalOpen, setIsOnboardingPackageModalOpen] =
    useState(false);
  const [isPayrollPartnerModalOpen, setIsPayrollPartnerModalOpen] =
    useState(false);
  const [payrollData, setPayrollData] = useState<PayrollPartners | null>(null);
  const [onboardingPackageData, setOnboardingPackageData] =
    useState<OnboardingPackage | null>(null);
  const { companyOnboardingDocs, isLoadingCompanyOnboardingDocs } =
    useGetCompanyOnboardingDocs({ setErrorText, companyId: selectedCompanyId });

  useEffect(() => {
    selectedCompanyId && setErrorText('');
  }, [selectedCompanyId]);
  if (isLoadingCompanyOnboardingDocs) {
    return <SpinLoader />;
  }

  return (
    <Stack
      bgcolor={white}
      spacing={3}
      p={3}
      pb={7}
      alignItems="center"
      justifyContent="space-between"
      borderRadius={8}
      flexGrow={1}
      width="100%"
      height="fit-content"
    >
      {errorText && <Alert severity="error">{errorText}</Alert>}
      {companyPlan === 2 ? (
        <>
          {!isLoadingCompanyOnboardingDocs &&
          companyOnboardingDocs &&
          companyOnboardingDocs?.templates?.length > 0 ? (
            <>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={3}
                bgcolor={white}
                py={3}
                width={1}
                borderRadius={8}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h3" fontWeight={medium}>
                  Onboarding Documents
                </Typography>
                <Button
                  onClick={() => setIsOnboardingPackageModalOpen(true)}
                  size="small"
                  variant="contained"
                  startIcon={<Add />}
                >
                  Add Documents
                </Button>
              </Stack>
              <EnterpriseOnboardingPackages
                templates={companyOnboardingDocs?.templates}
                setOnboardingPackageData={setOnboardingPackageData}
                setIsOnboardingPackageModalOpen={
                  setIsOnboardingPackageModalOpen
                }
                setIsDeletingModalOpen={setIsDeletingModalOpen}
              />
            </>
          ) : (
            <>
              <Stack spacing={5} py={5} alignItems="center">
                <Stack spacing={3} alignItems="center">
                  <ArticleOutlinedIcon sx={{ fontSize: '60px' }} />
                  <Typography variant="h3" fontWeight={medium}>
                    Onboarding packages
                  </Typography>
                  <Typography
                    maxWidth="530px"
                    textAlign="center"
                    color="text.secondary"
                    lineHeight={1.2}
                    fontWeight={medium}
                  >
                    <Typography
                      fontWeight={bold}
                      component="span"
                      sx={{ display: 'block' }}
                    >
                      Automate your onboarding!
                    </Typography>
                    Select or add custom documents here that will automatically
                    be sent with every accepted job offer. You can even
                    customize new hire packages based on specific positions.
                  </Typography>
                </Stack>
                <Button
                  onClick={() => setIsOnboardingPackageModalOpen(true)}
                  variant="contained"
                  startIcon={<Add />}
                >
                  Add Onboarding Package
                </Button>
              </Stack>
              <Divider sx={{ width: '100%' }} />
            </>
          )}
          {companyOnboardingDocs?.payroll_partner?.length > 0 ? (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={3}
              bgcolor={white}
              py={3}
              width={1}
              borderRadius={8}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h3" fontWeight={medium}>
                Payroll partners
              </Typography>
              <Button
                onClick={() => setIsPayrollPartnerModalOpen(true)}
                size="small"
                variant="contained"
                startIcon={<Add />}
              >
                Add Payroll Partner
              </Button>
            </Stack>
          ) : (
            <Stack spacing={3} alignItems="center">
              <MonitorOutlinedIcon sx={{ fontSize: '60px' }} />
              <Typography variant="h3" fontWeight={medium}>
                Payroll partners
              </Typography>
              <Typography
                maxWidth="530px"
                textAlign="center"
                color="text.secondary"
                lineHeight={1.2}
                fontWeight={medium}
              >
                Once the applicant has completed all of their documents, we can
                send the completed document package directly to your payroll
                partner
              </Typography>
            </Stack>
          )}
          {isLoadingCompanyOnboardingDocs ? (
            <Box>
              <CircularProgress />
            </Box>
          ) : (
            companyOnboardingDocs &&
            companyOnboardingDocs?.payroll_partner?.length > 0 && (
              <EnterpriseOnboardingPayrollPartners
                payrollPartners={companyOnboardingDocs?.payroll_partner}
                setPayrollData={setPayrollData}
                setIsPayrollPartnerModalOpen={setIsPayrollPartnerModalOpen}
                setIsDeletingModalOpen={setIsDeletingModalOpen}
              />
            )
          )}
          {companyOnboardingDocs?.payroll_partner?.length <= 0 && (
            <>
              <Button
                variant="contained"
                onClick={() => setIsPayrollPartnerModalOpen(true)}
              >
                Add Payroll Partner
              </Button>
              <Divider sx={{ width: '100%' }} />
            </>
          )}
        </>
      ) : (
        <Alert severity="info">
          Please upgrade location plan to access digital onboarding feature
        </Alert>
      )}

      <NewHireNotification companyId={selectedCompanyId} />
      {companyOnboardingDocs && isOnboardingPackageModalOpen && (
        <OnboardingPackageModal
          isOpen={isOnboardingPackageModalOpen}
          selectedCompanyId={selectedCompanyId}
          handleClose={() => {
            setIsOnboardingPackageModalOpen(false);
            setOnboardingPackageData(null);
          }}
          onboardingPackageData={onboardingPackageData}
        />
      )}
      {companyOnboardingDocs && isPayrollPartnerModalOpen && (
        <PayrollPartnerModal
          isOpen={isPayrollPartnerModalOpen}
          selectedCompanyId={selectedCompanyId}
          handleClose={() => {
            setPayrollData(null);
            setIsPayrollPartnerModalOpen(false);
          }}
          payrollData={payrollData}
        />
      )}
      <PayrollPartnerDeleteModal
        isOpen={isDeletingModalOpen}
        handleClose={() => {
          setIsDeletingModalOpen(false);
          setPayrollData(null);
          setOnboardingPackageData(null);
        }}
        payrollData={payrollData}
        selectedCompanyId={selectedCompanyId}
        onboardingPackageData={onboardingPackageData}
      />
    </Stack>
  );
};

export default EnterpriseOnboardingDocumnets;
